import React from 'react';
import { ShoppingBasket, X, Trash2, Check, Loader2, Minus, Plus } from 'lucide-react';
import FoodAvatar from './FoodAvatar';

/**
 * BasketBar — a temporary multi-food selection basket for Log Food.
 *
 * The `+` on Foods / Restaurant results adds items here instead of logging
 * immediately. A floating action (bottom-right, above the bottom nav, safe-area
 * aware) shows "Log N food(s)". Tapping opens a sheet to review/remove items,
 * then commit:
 *   - 1 item  -> a normal single food log (via onCommitSingle)
 *   - 2+ items -> one grouped custom meal (via onCommitGroup)
 *
 * The basket is only cleared by the parent after a SUCCESSFUL commit or an
 * explicit clear — never on a failed request or an accidental navigation.
 */
export default function BasketBar({ items, onRemove, onClear, onUpdateItem, onCommitSingle, onCommitGroup, committing }) {
  const [open, setOpen] = React.useState(false);
  const count = items.length;
  if (count === 0) return null;

  const totalCals = items.reduce(function (a, it) {
    const n = it.nutrition || {};
    const qty = (typeof it.serving_size === 'number' && it.serving_size > 0) ? it.serving_size : 1;
    return a + Math.round((n.calories || 0) * qty);
  }, 0);

  const commit = function () {
    if (count === 1) { onCommitSingle(items[0]); }
    else { onCommitGroup(items); }
  };

  return (
    <>
      {/* Floating action — above the bottom nav, safe-area aware */}
      <button
        type="button"
        onClick={function () { setOpen(true); }}
        className="fixed right-4 z-40 flex items-center gap-2 h-12 pl-4 pr-5 rounded-full bg-accent text-white font-semibold shadow-lg hover:bg-accent/90 transition-colors"
        style={{ bottom: 'calc(4rem + env(safe-area-inset-bottom) + 0.75rem)' }}
        data-testid="basket-fab"
      >
        <ShoppingBasket size={18} />
        Log {count} {count === 1 ? 'food' : 'foods'}
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-end" data-testid="basket-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={function () { setOpen(false); }} data-testid="basket-backdrop" />
          <div className="relative w-full bg-background rounded-t-2xl p-4 max-h-[75vh] overflow-y-auto shadow-xl" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
            <div className="w-10 h-1 bg-border rounded-full mx-auto mb-3" />
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-foreground">Selected foods · {count}</h2>
              <button type="button" onClick={function () { setOpen(false); }} className="size-8 rounded-full hover:bg-secondary flex items-center justify-center" data-testid="basket-close"><X size={18} /></button>
            </div>

            <ul className="divide-y divide-border">
              {items.map(function (it) {
                const n = it.nutrition || {};
                const qty = (typeof it.serving_size === 'number' && it.serving_size > 0) ? it.serving_size : 1;
                const cal = Math.round((n.calories || 0) * qty);
                const p = Math.round((n.protein_g || 0) * qty);
                const c = Math.round((n.carbs_g || 0) * qty);
                const f = Math.round((n.fat_g || 0) * qty);
                const opts = Array.isArray(it.serving_options) ? it.serving_options : [];
                const hasUnits = opts.length > 1;
                const setQty = function (next) {
                  const q = Math.max(0.5, Math.round(next * 10) / 10);
                  if (onUpdateItem) onUpdateItem(it._basketKey, { serving_size: q });
                };
                const changeUnit = function (e) {
                  const unit = e.target.value;
                  const opt = opts.find(function (o) { return o.unit === unit; });
                  if (opt && onUpdateItem) {
                    onUpdateItem(it._basketKey, {
                      serving_unit: opt.unit,
                      serving_size: (typeof opt.size === 'number' && opt.size > 0) ? opt.size : 1,
                      nutrition: opt.nutrition || it.nutrition,
                    });
                  }
                };
                return (
                  <li key={it._basketKey} className="flex items-start gap-3 py-3" data-testid="basket-item">
                    <FoodAvatar item={it} size={36} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{it.name}</p>
                      {/* Active amount + unit editing (mirrors FoodConfirm serving semantics) */}
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex items-center gap-1.5">
                          <button type="button" onClick={function () { setQty(qty - 0.5); }} disabled={qty <= 0.5} className="size-7 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground disabled:opacity-40" data-testid="basket-item-decrease"><Minus size={13} /></button>
                          <span className="min-w-[2ch] text-center text-sm font-mono font-semibold text-foreground" data-testid="basket-item-qty">{qty}</span>
                          <button type="button" onClick={function () { setQty(qty + 0.5); }} className="size-7 rounded-full border border-border/60 flex items-center justify-center text-muted-foreground" data-testid="basket-item-increase"><Plus size={13} /></button>
                        </div>
                        {hasUnits ? (
                          <select value={it.serving_unit || ''} onChange={changeUnit} className="text-xs bg-secondary/60 rounded-md px-2 py-1 text-foreground focus:outline-none" data-testid="basket-item-unit">
                            {opts.map(function (o) { return <option key={o.unit} value={o.unit}>{o.unit}</option>; })}
                          </select>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid="basket-item-unit">{it.serving_unit || 'serving'}</span>
                        )}
                      </div>
                      {/* Calories + P/C/F for the active amount */}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1" data-testid="basket-item-macros">
                        <span className="font-medium text-foreground">{cal} cal</span>
                        <span>{p}g P</span>
                        <span>{c}g C</span>
                        <span>{f}g F</span>
                      </div>
                    </div>
                    <button type="button" onClick={function () { onRemove(it._basketKey); }} className="size-8 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground hover:text-destructive" aria-label={'Remove ' + it.name} data-testid="basket-item-remove"><Trash2 size={15} /></button>
                  </li>
                );
              })}
            </ul>

            <p className="text-xs text-muted-foreground text-center mt-3">
              {count === 1 ? 'Logs as a single food.' : 'Logs as one meal with ' + count + ' components (~' + totalCals + ' cal).'}
            </p>

            <div className="flex gap-2 mt-3">
              <button type="button" onClick={onClear} className="h-11 px-4 rounded-xl border border-border/50 text-sm font-medium text-muted-foreground" data-testid="basket-clear">Clear</button>
              <button
                type="button"
                onClick={commit}
                disabled={committing}
                className="flex-1 h-11 rounded-xl bg-accent text-white text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                data-testid="basket-commit"
              >
                {committing ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                Log meal · {totalCals} cal
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
